import React, {useEffect, useRef, useState} from 'react';

import {toInteger} from 'lodash';
import styled from 'styled-components';
import uniqolor from 'uniqolor';

import {InlineCommentsPopover} from 'app/comments/components/InlineCommentsPopover';
import {useTextHighlights} from 'app/comments/TextHighlightsContextProvider';
import {useCurrentUserState} from 'app/current-user/api';
import {htmlToTokens} from 'app/string/htmldiff';
import {gt} from 'app/utils/i18n';

import {AttachOverlay} from './AttachOverlay';

import type {CommentableContentType, CommentHighlightParams} from 'sweet-types';

function extractNodeParams(
  node: HTMLElement | null | undefined,
  offset: number
): CommentHighlightParams | null {
  if (node == null) {
    return null;
  }
  const dataHtmldiffOffset = toInteger(
    node.getAttribute('data-htmldiff-offset') || '0'
  );
  const tokensStartText = node.getAttribute('data-htmldiff-start');
  const tokensEndText = node.getAttribute('data-htmldiff-end');

  if (tokensStartText == null || tokensEndText == null) {
    return null;
  }
  const innerText = node.innerText;
  const innerTextTokens = htmlToTokens(innerText);

  let tokensStart = toInteger(tokensStartText);
  let tokensEnd = toInteger(tokensEndText);
  const tokensDiffLength = tokensEnd - tokensStart;

  if (innerTextTokens.length < tokensDiffLength) {
    // This means that the ``innerText`` is wrapped in external html tag. Actual
    // text starts at tokensStart + 1, and ends at tokensEnd - 1.
    tokensStart += 1;
    tokensEnd -= 1;
  }

  let tokensOffset = 0;
  let tokenTextOffset = dataHtmldiffOffset;
  if (offset > 0) {
    const selectionTokensStart = htmlToTokens(innerText.substring(0, offset));
    tokensOffset = selectionTokensStart.length - 1;
    // Measuring where the selection starts (or ends) based on the position of
    // tokensStart + tokensOffset (textOffset might split a token right in the
    // middle)
    if (tokensOffset !== 0) {
      tokenTextOffset = 0;
    }
    tokenTextOffset += selectionTokensStart[tokensOffset].string.length;
  }

  return {
    tokensStart,
    tokensEnd,
    tokensOffset,
    tokenTextOffset,
    token: tokensStart + tokensOffset,
  };
}

interface Props {
  targetRef: HTMLDivElement;
  objectId: number;
  versionId: number;
  contentType: CommentableContentType;
  stepId: string;
}

export function TextSelectionPopover({
  targetRef,
  objectId,
  versionId,
  contentType,
  stepId,
}: Props) {
  const {addHighlight, clearHighlight, highlights, getTextFromTokens} =
    useTextHighlights();

  const {name: userName} = useCurrentUserState().data!;

  const conversationId = useRef<string | null>(null);

  const selection = useRef<Selection | null>(null);
  const [position, setPosition] = useState<{x: number; y: number} | null>(null);

  const lastSelectionRange = useRef<Array<Range>>([]);

  useEffect(() => {
    if (position == null && conversationId.current != null) {
      clearHighlight(conversationId.current);
      conversationId.current = null;
    }
  }, [position]);

  useEffect(() => {
    if (
      conversationId.current != null &&
      highlights[conversationId.current] == null
    ) {
      conversationId.current = null;
      resetPendingHighlight();
    }
  }, [highlights]);

  useEffect(() => {
    targetRef.addEventListener('selectstart', onSelectionStart);
    document.addEventListener('mouseup', onSelectionEnd);
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      targetRef.removeEventListener('selectstart', onSelectionStart);
      document.removeEventListener('mouseup', onSelectionEnd);
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, []);

  function resetPendingHighlight() {
    setPosition(null);
    selection.current = null;
    lastSelectionRange.current = [];
  }

  function onAddHighlight(event: React.MouseEvent) {
    event.preventDefault();
    if (!selection.current || selection.current.rangeCount === 0) {
      return;
    }

    // Find the exact row and absolute offsets
    let startOffset = selection.current.anchorOffset;
    let endOffset = selection.current.focusOffset;

    const startParent = selection.current.anchorNode?.parentElement;
    const endParent = selection.current.focusNode?.parentElement || startParent;

    let startParams = extractNodeParams(startParent, startOffset);
    let endParams = extractNodeParams(endParent, endOffset);

    if (startParams == null || endParams == null) {
      // Early return - cannot extract params information from the selection
      if (conversationId.current != null) {
        clearHighlight(conversationId.current);
        conversationId.current = null;
      }
      resetPendingHighlight();
      return;
    }

    if (
      startParams.token > endParams.token ||
      (startParams.token === endParams.token &&
        startParams.tokenTextOffset > endParams.tokenTextOffset)
    ) {
      const tempParams = startParams;
      startParams = endParams;
      endParams = tempParams;
    }

    const selectedText = getTextFromTokens(
      versionId,
      stepId,
      startParams.token,
      startParams.tokenTextOffset,
      endParams.token,
      endParams.tokenTextOffset
    );

    if (selectedText == null) {
      return;
    }

    if (conversationId.current != null) {
      clearHighlight(conversationId.current);
      conversationId.current = null;
    }
    const color = uniqolor(userName, {
      saturation: 50,
      lightness: 65,
    }).color;

    conversationId.current = addHighlight(
      null,
      startParams,
      endParams,
      versionId,
      contentType,
      selectedText,
      stepId,
      color
    );
  }

  function onSelectionStart() {
    const activeSelection = document.getSelection();
    if (
      activeSelection != null &&
      targetRef.contains(activeSelection.anchorNode)
    ) {
      resetPendingHighlight();
    }
  }

  function onSelectionChange() {
    const activeSelection = document.getSelection();
    if (
      activeSelection != null &&
      activeSelection.anchorNode != null &&
      activeSelection.focusNode != null &&
      targetRef.contains(activeSelection.anchorNode)
    ) {
      // Check if focus node is
      if (!targetRef.contains(activeSelection.focusNode)) {
        if (lastSelectionRange.current.length > 0) {
          activeSelection.removeAllRanges();
          lastSelectionRange.current.forEach((range) => {
            activeSelection.addRange(range);
          });
        }
      } else {
        lastSelectionRange.current = [];
        for (let i = 0; i < activeSelection.rangeCount; i += 1) {
          lastSelectionRange.current.push(activeSelection.getRangeAt(i));
        }
      }
    }
  }

  function onSelectionEnd() {
    if (selection.current != null) {
      resetPendingHighlight();
      return;
    }

    if (conversationId.current != null) {
      return;
    }

    const activeSelection = document.getSelection();
    const text = activeSelection?.toString();

    if (!activeSelection || !text) {
      resetPendingHighlight();
      return;
    }

    if (
      targetRef.contains(activeSelection.anchorNode) &&
      targetRef.contains(activeSelection.focusNode)
    ) {
      selection.current = activeSelection;

      const range = activeSelection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = targetRef.getBoundingClientRect();

      const offsetX = targetRef.offsetLeft;
      const offsetY = targetRef.offsetTop;

      setPosition({
        x: rect.left - containerRect.left + offsetX,
        y: rect.top - containerRect.top + offsetY - 30,
      });
    } else {
      resetPendingHighlight();
    }
  }

  return position != null ? (
    <div
      onMouseUp={(event) => {
        // Stop propagation on up event as that will trigger the onSelectionEnd function
        event.stopPropagation();
      }}
      onClick={(event) => {
        // Stop propagation on up event as that will trigger the onSelectionEnd function
        event.stopPropagation();
      }}
    >
      <AttachOverlay
        trigger="click"
        overlay={({overlayProps}) => {
          return (
            <InlineCommentsPopover
              contentType={contentType}
              objectId={objectId}
              versionId={versionId}
              overlayProps={overlayProps}
              highlight={
                conversationId.current != null
                  ? highlights[conversationId.current]
                  : null
              }
            />
          );
        }}
      >
        {(handlers) => (
          <StyledPositionedDiv top={position.y} left={position.x}>
            <button
              className="btn btn-link btn-sm dropdown-item"
              type="button"
              {...handlers}
              onClick={(event: React.MouseEvent) => {
                onAddHighlight(event);
                handlers.onClick?.();
              }}
            >
              <i className="far fa-comment" /> {gt('Comment')}
            </button>
          </StyledPositionedDiv>
        )}
      </AttachOverlay>
    </div>
  ) : null;
}

export const StyledPositionedDiv = styled.div<{
  top: number;
  left: number;
  maxWidth?: number;
  minWidth?: number;
}>`
  position: absolute;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  background: white;
  ${(props) =>
    props.maxWidth !== undefined ? `max-width: ${props.maxWidth}px;` : ''}
  ${(props) =>
    props.minWidth !== undefined ? `min-width: ${props.minWidth}px;` : ''}
  z-index: 1000;
  transition: opacity 0.2s ease-in-out;

  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
`;
